import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { Check, ChevronsUpDown, Folder, Plus } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { RadioGroup, RadioGroupIndicator } from "~/components/ui/radio-group";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { nextgenConversationPath } from "~/pages/manifold-nextgen/paths";
import { Agent, AgentStatus } from "~/services/agents";
import { createProjectConversation, projectConversationsPath } from "~/services/projects";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

interface AgentListProps {
  agents: Agent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const AgentList = ({ agents, selectedId, onSelect }: AgentListProps) => (
  <RadioGroup value={selectedId ?? ""} onValueChange={onSelect} className="block">
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
      {agents.map((agent) => (
        <li key={agent.id}>
          <label className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/50">
            <RadioGroupIndicator value={agent.id} />
            <AgentAvatar id={agent.id} name={agent.name} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{agent.display_name}</p>
              {agent.description && (
                <p className="truncate text-xs text-muted-foreground">{agent.description}</p>
              )}
            </div>
          </label>
        </li>
      ))}
    </ul>
  </RadioGroup>
);

interface DatasetComboboxProps {
  datasets: Dataset[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const DatasetCombobox = ({ datasets, selectedId, onSelect }: DatasetComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = datasets.find((d) => d.id === selectedId);
  const filtered = query
    ? datasets.filter((d) => d.title.toLowerCase().includes(query.toLowerCase()))
    : datasets;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate">{selected.title}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">Select a dataset…</span>
          )}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command>
          <CommandInput placeholder="Search datasets…" onValueChange={setQuery} />
          <CommandList>
            {filtered.length === 0 && <CommandEmpty>No datasets found.</CommandEmpty>}
            {filtered.map((dataset) => (
              <CommandItem
                key={dataset.id}
                value={dataset.title}
                className="gap-2"
                onSelect={() => {
                  onSelect(dataset.id);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{dataset.title}</span>
                <Check
                  className={cn(
                    "ml-auto size-4 shrink-0",
                    selectedId === dataset.id ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const NewSessionPicker = () => {
  const { projectId, attachedAgents, attachedDatasets } = useProjectContext();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [agentsDialogOpen, setAgentsDialogOpen] = useState(false);
  const [datasetsDialogOpen, setDatasetsDialogOpen] = useState(false);

  const enabledAgents = useMemo(
    () => (attachedAgents ?? []).filter((a) => a.status !== AgentStatus.DISABLED),
    [attachedAgents]
  );

  useEffect(() => {
    if (attachedAgents !== undefined) {
      setSelectedAgentId(enabledAgents.length > 0 ? enabledAgents[0].id : null);
    }
    if (attachedDatasets !== undefined) {
      setSelectedDatasetId(attachedDatasets.length === 1 ? attachedDatasets[0].id : null);
    }
  }, [attachedAgents, attachedDatasets, enabledAgents]);

  const canStart =
    enabledAgents.length > 0 &&
    (attachedDatasets?.length ?? 0) > 0 &&
    !!selectedAgentId &&
    !!selectedDatasetId;

  const handleStart = async () => {
    if (!selectedAgentId || !selectedDatasetId) {
      return;
    }
    setIsPending(true);
    try {
      const conversation = await createProjectConversation(projectId, {
        agent_id: selectedAgentId,
        dataset_id: selectedDatasetId,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await mutate(projectConversationsPath(projectId));
      // Flag the conversation as new so the route shows the centered empty-state
      // skeleton while /connect loads, instead of flashing the bottom-composer
      // layout before snapping to the centered view.
      navigate(nextgenConversationPath(projectId, conversation.id), { state: { isNew: true } });
    } catch {
      showError("Failed to start session. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <>
      <div className="flex flex-1 flex-col items-center overflow-y-auto p-8">
        <div className="my-auto flex w-full max-w-lg flex-col gap-6">
          <div>
            <h1 className="text-xl font-semibold">New Session</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Select an agent and a dataset to get started.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Agents
              </p>
              {enabledAgents.length === 0 ? (
                <div className="flex items-center justify-center rounded-lg border border-dashed border-border px-4 py-6">
                  <p className="text-xs text-muted-foreground">No enabled agents attached.</p>
                </div>
              ) : (
                <AgentList
                  agents={enabledAgents}
                  selectedId={selectedAgentId}
                  onSelect={setSelectedAgentId}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto self-start py-0.5 text-xs text-primary hover:text-primary/80"
                onClick={() => setAgentsDialogOpen(true)}
              >
                <Plus className="size-3.5" />
                Add Agents
              </Button>
            </div>

            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Datasets
              </p>
              {(attachedDatasets ?? []).length === 0 ? (
                <div className="flex items-center justify-center rounded-lg border border-dashed border-border px-4 py-6">
                  <p className="text-xs text-muted-foreground">No datasets attached yet.</p>
                </div>
              ) : (
                <DatasetCombobox
                  datasets={attachedDatasets ?? []}
                  selectedId={selectedDatasetId}
                  onSelect={setSelectedDatasetId}
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto self-start py-0.5 text-xs text-primary hover:text-primary/80"
                onClick={() => setDatasetsDialogOpen(true)}
              >
                <Plus className="size-3.5" />
                Add Datasets
              </Button>
            </div>
          </div>

          <Button onClick={handleStart} disabled={!canStart || isPending} className="self-end">
            {isPending ? "Starting…" : "Start"}
          </Button>
        </div>
      </div>

      <ProjectAgentsDialog
        projectId={projectId}
        open={agentsDialogOpen}
        onOpenChange={setAgentsDialogOpen}
        attached={attachedAgents ?? []}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={datasetsDialogOpen}
        onOpenChange={setDatasetsDialogOpen}
        attachedIds={attachedDatasets?.map((d) => d.id) ?? []}
      />
    </>
  );
};
